import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

const STOCK_STATUSES = [
  { value: "instock", label: "Còn hàng" },
  { value: "outofstock", label: "Hết hàng" },
];

const ProductStockForm = ({ product, suppliers, setPopupShow, refreshList }) => {
  const [stockEntries, setStockEntries] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (product?.supplierStock?.length > 0) {
      setStockEntries(
        product.supplierStock.map((s) => ({
          supplier: s.supplier?._id || s.supplier,
          status: s.status || "instock",
          note: s.note || "",
        }))
      );
    } else {
      setStockEntries([]);
    }
  }, [product]);

  const addEntry = () => {
    setStockEntries((prev) => [...prev, { supplier: "", status: "instock", note: "" }]);
  };

  const removeEntry = (index) => {
    setStockEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEntryChange = (index, field, value) => {
    setStockEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Validate no duplicate suppliers
      const supplierIds = stockEntries.map((s) => s.supplier).filter(Boolean);
      const hasDuplicates = new Set(supplierIds).size !== supplierIds.length;
      if (hasDuplicates) {
        toast.warning("Mỗi supplier chỉ được chọn một lần");
        setSubmitting(false);
        return;
      }

      const validEntries = stockEntries.filter((s) => s.supplier);

      const res = await Utils._apiRequest({
        path: `${Enums.endpoints.supplierStock.PRODUCTS}/${product._id}/stock`,
        type: "PUT",
        payload: { supplierStock: validEntries },
      });

      if (res.errors) throw new Error(res.errors);
      toast.success("Cập nhật stock thành công");
      refreshList();
      setPopupShow(false);
    } catch (error) {
      toast.error(error.message || "Lỗi khi cập nhật stock");
    } finally {
      setSubmitting(false);
    }
  };

  // Filter out already-selected suppliers for each row
  const getAvailableSuppliers = (currentIndex) => {
    const selectedIds = stockEntries
      .map((s, i) => (i !== currentIndex ? s.supplier : null))
      .filter(Boolean);
    return suppliers.filter((s) => !selectedIds.includes(s._id));
  };

  return (
    <Form onSubmit={handleSubmit} className="supplier-stock-form">
      <section className="supplier-stock-form__intro">
        <span className="supplier-stock-form__eyebrow">Quản lý stock</span>
        <h3>Cập nhật tình trạng stock</h3>
        <p>
          Sản phẩm: <strong>{product?.productName}</strong> (SKU: <Badge bg="secondary">{product?.sku}</Badge>)
        </p>
      </section>

      <div className="supplier-stock-form__toolbar d-flex justify-content-between align-items-center mb-3 mt-3">
        <div>
          <h6 className="mb-1 fw-bold">Tình trạng từng supplier</h6>
          <p className="supplier-stock-form__toolbar-copy mb-0">
            Thêm supplier và cập nhật trạng thái hàng hoá (còn hàng / hết hàng / ghi chú size...).
          </p>
        </div>
        <Button variant="outline-primary" size="sm" onClick={addEntry} type="button">
          <FontAwesomeIcon icon={faPlus} className="me-2" /> Thêm supplier
        </Button>
      </div>

      {stockEntries.length === 0 && (
        <div className="supplier-stock-form__empty-stock text-center py-4">
          <p className="text-muted mb-0">Chưa có thông tin stock cho sản phẩm này. Nhấn "Thêm supplier" để bắt đầu.</p>
        </div>
      )}

      {stockEntries.map((entry, index) => (
        <Card className="mb-3 shadow-sm supplier-stock-language-card" key={index}>
          <Card.Body>
            <div className="supplier-stock-language-card__header">
              <div>
                <span className="supplier-stock-language-card__label">Supplier {index + 1}</span>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeEntry(index)}
                type="button"
              >
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Xoá
              </Button>
            </div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Supplier *</Form.Label>
                  <Form.Select
                    value={entry.supplier}
                    onChange={(e) => handleEntryChange(index, "supplier", e.target.value)}
                    required
                  >
                    <option value="">-- Chọn supplier --</option>
                    {getAvailableSuppliers(index).map((s) => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                    {/* Keep current selected if it's not in available */}
                    {entry.supplier && !getAvailableSuppliers(index).find((s) => s._id === entry.supplier) && (
                      <option value={entry.supplier}>
                        {suppliers.find((s) => s._id === entry.supplier)?.name || entry.supplier}
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Trạng thái *</Form.Label>
                  <Form.Select
                    value={entry.status}
                    onChange={(e) => handleEntryChange(index, "status", e.target.value)}
                    required
                  >
                    {STOCK_STATUSES.map((st) => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Ghi chú</Form.Label>
                  <Form.Control
                    type="text"
                    value={entry.note}
                    onChange={(e) => handleEntryChange(index, "note", e.target.value)}
                    placeholder="VD: Max size EU45, Min size EU39..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))}

      <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4 supplier-stock-form__actions">
        <Button
          variant="secondary"
          className="me-md-2"
          onClick={() => setPopupShow(false)}
          disabled={submitting}
          type="button"
        >
          Huỷ
        </Button>
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? "Đang lưu..." : "Lưu Stock"}
        </Button>
      </div>
    </Form>
  );
};

export default ProductStockForm;
